import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import type { HostelStatsEntry } from './types';

function fmtShort(n: number | null | undefined) {
  const v = isNaN(Number(n)) ? 0 : (Number(n) ?? 0);
  if (v >= 100000) return '₹' + (v / 100000).toFixed(1) + 'L';
  if (v >= 1000) return '₹' + (v / 1000).toFixed(1) + 'k';
  return '₹' + v.toLocaleString('en-IN');
}

interface PropertyOverviewTableProps {
  entries: HostelStatsEntry[];
  role: 'owner' | 'supervisor';
}

export function PropertyOverviewTable({ entries, role }: PropertyOverviewTableProps) {
  const { t } = useTranslation();

  if (entries.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.propertyOverview')}</h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('hostel.hostelName')}</TableHead>
            <TableHead className="text-center">{t('dashboard.occupancyRate')}</TableHead>
            <TableHead className="text-center">{t('dashboard.revenue')}</TableHead>
            <TableHead className="text-center">{t('dashboard.pendingRent')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map(({ hostel, stats }) => (
            <TableRow key={hostel.id}>
              <TableCell className="font-medium text-gray-900">
                {role === 'owner' ? (
                  <Link to="/owner/hostels/$id" params={{ id: hostel.id }} search={{ bedStatus: '' }} className="hover:underline">
                    {hostel.name}
                  </Link>
                ) : (
                  <Link to="/supervisor/hostels/$id" params={{ id: hostel.id }} search={{ bedStatus: '' }} className="hover:underline">
                    {hostel.name}
                  </Link>
                )}
              </TableCell>
              <TableCell className="text-center">{stats.occupiedBeds}/{stats.totalBeds}</TableCell>
              <TableCell className="text-center">{fmtShort(stats.collectedThisMonth)}</TableCell>
              <TableCell className="text-center">{fmtShort(stats.pendingAmount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
